import { canConfigureRms, canViewRms } from "../rms/rmsAccessControl";

export const MODULE_KEYS = Object.freeze([
  "pms",
  "purchasing",
  "pos",
  "analytics",
  "onboarding",
  "crm",
  "corporate_purchasing",
]);

const MODULE_KEY_SET = new Set(MODULE_KEYS);
const MODULE_KEY_ALIASES = Object.freeze({
  control_gestion: "analytics",
});

const RESERVATION_SETTINGS_PERMISSIONS = Object.freeze([
  "hotel_settings.view_customer_settings",
  "hotel_settings.view_stay_settings",
  "hotel_settings.view_reservation_flow",
  "hotel_settings.view_required_fields",
  "hotel_settings.view_required_roles",
]);

const hasPermission = (permissions, key, isAdmin) =>
  Boolean(isAdmin) || permissions?.[key] === true;

const hasAnyPermission = (permissions, keys, isAdmin) =>
  keys.some((key) => hasPermission(permissions, key, isAdmin));

export function normalizeAllowedModules(modules) {
  if (!Array.isArray(modules)) return [];

  return Array.from(
    new Set(
      modules
        .map((moduleKey) => String(moduleKey || "").trim().toLowerCase())
        .map((moduleKey) => MODULE_KEY_ALIASES[moduleKey] || moduleKey)
        .filter((moduleKey) => MODULE_KEY_SET.has(moduleKey))
    )
  );
}

export function resolveFirstPmsEntryPath(permissions = {}, { isAdmin = false } = {}) {
  const can = (key) => hasPermission(permissions, key, isAdmin);

  if (can("reservations.view_dashboard")) return "/reservations/dashboard";
  if (can("reservations.view_list") || can("reservations.create")) {
    return "/reservations/list";
  }
  if (can("reservations.view_availability")) return "/reservations/availability";
  if (can("front_desk.view_check_in")) return "/front-desk/check-ins";
  if (can("front_desk.view_check_out")) return "/front-desk/check-outs";
  if (can("rooms.view_matrix")) return "/rooms-management/matrix";
  if (can("rooms.view_assignment")) return "/rooms-management/assignment";
  if (can("rooms.view_blocks")) return "/rooms-management/blocks";
  if (can("revenue.view_room_rates")) {
    return "/revenue-management-container/management";
  }
  if (canViewRms(permissions)) {
    return canConfigureRms(permissions)
      ? "/revenue-management-container/revenue-optimizer/setup"
      : "/revenue-management-container/revenue-optimizer/signals-forecast";
  }
  if (can("reviews_intelligence.access")) return "/reviews-intelligence/dashboard";
  if (can("reports.view")) return "/reports/monthly-stay-performance";

  if (hasAnyPermission(permissions, RESERVATION_SETTINGS_PERMISSIONS, isAdmin)) {
    return "/settings/reservation";
  }
  if (can("hotel_settings.view_front_desk_settings")) return "/settings/front-desk";
  if (can("hotel_settings.view_revenue_settings")) return "/settings/revenue";
  if (can("hotel_settings.view_extras_tab")) return "/settings/extras";
  if (can("hotel_settings.view_reservation_charges_tab")) return "/settings/charges";
  if (can("hotel_settings.view_booking_engine_tab")) return "/settings/booking-engine";
  if (
    can("access_settings") ||
    can("finance.settings.view") ||
    can("finance.settings.manage_legal_entity") ||
    can("finance.settings.manage_taxes")
  ) return "/settings/legal-entity";

  return null;
}

export function resolveGlobalSettingsEntryPath(permissions = {}, { isAdmin = false, corporateAccess = false } = {}) {
  return corporateAccess || hasPermission(permissions, "hotel_settings.view_users_tab", isAdmin)
    ? "/settings/users"
    : null;
}

export function addBusinessUnitToPath(path, businessUnitId) {
  if (!path) return null;

  const normalizedBusinessUnitId = String(businessUnitId ?? "").trim();
  if (!normalizedBusinessUnitId) return path;

  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}business_unit_id=${encodeURIComponent(normalizedBusinessUnitId)}`;
}

export function resolveModuleEntryPath({
  moduleKey,
  permissions = {},
  businessUnitId,
  isAdmin = false,
} = {}) {
  const normalizedModuleKey = normalizeAllowedModules([moduleKey])[0];

  switch (normalizedModuleKey) {
    case "pms":
      return addBusinessUnitToPath(
        resolveFirstPmsEntryPath(permissions, { isAdmin }),
        businessUnitId
      );
    case "purchasing":
      return "/purchasing/dashboard";
    case "corporate_purchasing":
      return "/corporate-purchasing/tenders";
    case "pos":
      return "/pos/sales";
    case "analytics":
      return "/analytics/sales";
    case "onboarding":
      return "/onboarding/language";
    case "crm":
      return "/crm/dashboard";
    default:
      return null;
  }
}

export function resolveModuleTargetAfterLogin({
  modules = [],
  permissions = {},
  businessUnitId,
  isAdmin = false,
} = {}) {
  const moduleTargets = normalizeAllowedModules(modules)
    .filter((moduleKey) => moduleKey !== "corporate_purchasing")
    .map((moduleKey) => resolveModuleEntryPath({
      moduleKey,
      permissions,
      businessUnitId,
      isAdmin,
    }))
    .filter(Boolean);
  const globalSettingsTarget = addBusinessUnitToPath(
    resolveGlobalSettingsEntryPath(permissions, { isAdmin }),
    businessUnitId
  );

  if (moduleTargets.length > 1) {
    return "/select-module";
  }
  if (moduleTargets.length === 1) return moduleTargets[0];

  return globalSettingsTarget;
}
